// src/realtime/pgbroadcast.ts
export type PGBroadcastEvent =
  | { type: "change"; topic: string; data: any }
  | { type: "presence"; topic: string; data: any };

type Handler = (ev: PGBroadcastEvent) => void;

export type PGBroadcastStatus =
  | "idle"
  | "no_token"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

type StatusHandler = (s: PGBroadcastStatus) => void;

export class PGBroadcastClient {
  private ws?: WebSocket;
  private handlers = new Set<Handler>();
  private statusHandlers = new Set<StatusHandler>();

  private url: string;
  private getToken: () => string | undefined;

  private topics = new Set<string>();
  private reconnectTimer?: number;

  private reconnectDelayMs = 1000;
  private status: PGBroadcastStatus = "idle";

  constructor(url: string, getToken: () => string | undefined) {
    this.url = url;
    this.getToken = getToken;
  }

  onMessage(fn: Handler) {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  onStatus(fn: StatusHandler) {
    this.statusHandlers.add(fn);
    // push current status immediately for convenience
    try {
      fn(this.status);
    } catch {}
    return () => this.statusHandlers.delete(fn);
  }

  getStatus(): PGBroadcastStatus {
    return this.status;
  }

  private setStatus(s: PGBroadcastStatus) {
    if (this.status === s) return;
    this.status = s;
    for (const h of this.statusHandlers) {
      try {
        h(s);
      } catch {}
    }
  }

  connect() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const token = this.getToken?.();
    if (!token) {
      // no token means we should not attempt WS at all
      this.setStatus("no_token");
      console.warn("[pgbroadcast] no token; not connecting");
      return;
    }

    this.setStatus("connecting");

    const wsUrl = `${this.url}${this.url.includes("?") ? "&" : "?"}access_token=${encodeURIComponent(token)}`;
    console.log("[pgbroadcast] connecting", "to wsUrl");

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.reconnectDelayMs = 1000; // reset backoff on successful connect
      this.setStatus("connected");

      console.log("[pgbroadcast] ws open");
      if (this.topics.size) {
        console.log("[pgbroadcast] resubscribing on open", [...this.topics]);
        this.sendSub([...this.topics]);
      }
    };

    this.ws.onmessage = (e) => {
      try {
        const ev = JSON.parse(String(e.data));
        // console.log("[pgbroadcast] message", ev);
        for (const h of this.handlers) h(ev);
      } catch (err) {
        console.warn("[pgbroadcast] invalid message", e.data, err);
      }
    };

    this.ws.onclose = (e) => {
      console.warn("[pgbroadcast] ws closed", e.code, e.reason);
      this.ws = undefined;
      this.setStatus("disconnected");

      if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);

      const delay = this.reconnectDelayMs;
      this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 30_000);

      this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    };

    this.ws.onerror = (e) => {
      console.warn("[pgbroadcast] ws error", e);
      this.setStatus("error");
      // onclose will handle reconnect if the socket closes
    };
  }

  close() {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.ws?.close();
    this.ws = undefined;
    this.setStatus("disconnected");
  }

  subscribe(topic: string) {
    if (this.topics.has(topic)) return; // already subscribed -> no-op
    this.topics.add(topic);
    this.connect();
    if (this.ws?.readyState === WebSocket.OPEN) this.sendSub([topic]);
  }

  unsubscribe(topic: string) {
    if (!this.topics.has(topic)) return;
    this.topics.delete(topic);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "unsub", topics: [topic] }));
    }
  }

  private sendSub(topics: string[]) {
    this.ws?.send(JSON.stringify({ type: "sub", topics }));
  }
}
